import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ScrollText } from "lucide-react";

const RESOURCE_LABELS = {
  tenant: "Inquilino", contract: "Contrato", payment: "Pago", expense: "Gasto",
  incident: "Incidencia", incident_photo: "Foto incidencia", property: "Piso",
  user: "Usuario", backup: "Backup", import: "Importación Excel", contract_document: "Documento de contrato",
};
const ACTION_META = {
  create: { label: "Creado", cls: "bg-success/15 text-success" },
  update: { label: "Modificado", cls: "bg-primary/15 text-primary" },
  delete: { label: "Borrado", cls: "bg-destructive/15 text-destructive" },
};

function fechaHora(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Muestra un valor de un diff de forma legible: objetos/arrays como JSON
// corto, primitivos tal cual, vacío como "—".
function fmtValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export default function Auditoria() {
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const [resourceFilter, setResourceFilter] = useState("todos");
  const [propertyFilter, setPropertyFilter] = useState("todos");
  const [actionFilter, setActionFilter] = useState("todos");
  const [accessFilter, setAccessFilter] = useState("todos");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/properties"),
      api.get("/users"),
      api.get("/audit-log"),
      api.get("/access-log"),
    ])
      .then(([p, u, a, acc]) => {
        setProperties(p.data);
        setUsers(u.data);
        setAuditLog(a.data);
        setAccessLog(acc.data);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const propName = (id) => properties.find((p) => p.id === id)?.nombre || id || "—";
  const userName = (id) => users.find((u) => u.user_id === id)?.name || null;

  const filteredAudit = useMemo(() => {
    return auditLog.filter((e) => {
      if (resourceFilter !== "todos" && e.resource_type !== resourceFilter) return false;
      if (propertyFilter !== "todos" && e.property_id !== propertyFilter) return false;
      if (actionFilter !== "todos" && e.action !== actionFilter) return false;
      return true;
    });
  }, [auditLog, resourceFilter, propertyFilter, actionFilter]);

  const filteredAccess = useMemo(() => {
    if (accessFilter === "todos") return accessLog;
    const wantSuccess = accessFilter === "exito";
    return accessLog.filter((e) => e.success === wantSuccess);
  }, [accessLog, accessFilter]);

  const resourceTypesPresent = useMemo(
    () => [...new Set(auditLog.map((e) => e.resource_type))].sort(),
    [auditLog]
  );

  return (
    <div data-testid="auditoria-page">
      <PageHeader title="Auditoría" subtitle="Quién entró y qué se modificó" />

      <Tabs defaultValue="cambios">
        <TabsList>
          <TabsTrigger value="cambios" data-testid="tab-cambios">Cambios de datos</TabsTrigger>
          <TabsTrigger value="accesos" data-testid="tab-accesos">Accesos</TabsTrigger>
        </TabsList>

        <TabsContent value="cambios">
          <div className="flex flex-wrap gap-3 mb-3">
            <Select value={resourceFilter} onValueChange={setResourceFilter}>
              <SelectTrigger className="w-48" data-testid="filter-resource"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los tipos</SelectItem>
                {resourceTypesPresent.map((rt) => (
                  <SelectItem key={rt} value={rt}>{RESOURCE_LABELS[rt] || rt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger className="w-48" data-testid="filter-property"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los pisos</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-40" data-testid="filter-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Toda acción</SelectItem>
                <SelectItem value="create">Creado</SelectItem>
                <SelectItem value="update">Modificado</SelectItem>
                <SelectItem value="delete">Borrado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="data-card overflow-x-auto">
            <Table data-testid="audit-log-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Acción</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Piso</TableHead>
                  <TableHead>Detalle</TableHead>
                  <TableHead className="hidden lg:table-cell">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>
                ) : filteredAudit.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sin registros con estos filtros.</TableCell></TableRow>
                ) : (
                  filteredAudit.map((e) => {
                    const meta = ACTION_META[e.action] || { label: e.action, cls: "bg-secondary text-secondary-foreground" };
                    const fieldNames = e.changes ? Object.keys(e.changes) : [];
                    return (
                      <TableRow
                        key={e.id}
                        onClick={() => setDetail(e)}
                        className="cursor-pointer hover:bg-secondary/40"
                        data-testid={`audit-row-${e.id}`}
                      >
                        <TableCell className="font-mono text-xs whitespace-nowrap">{fechaHora(e.timestamp)}</TableCell>
                        <TableCell className="text-sm">{userName(e.user_id) || e.user_email || "—"}</TableCell>
                        <TableCell><Badge className={`${meta.cls} hover:${meta.cls} border-0`}>{meta.label}</Badge></TableCell>
                        <TableCell className="text-sm">{RESOURCE_LABELS[e.resource_type] || e.resource_type}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{e.property_id ? propName(e.property_id) : "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                          {e.summary || (fieldNames.length ? `Cambió: ${fieldNames.join(", ")}` : "—")}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell font-mono text-xs text-muted-foreground">{e.ip || "—"}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="accesos">
          <div className="flex flex-wrap gap-3 mb-3">
            <Select value={accessFilter} onValueChange={setAccessFilter}>
              <SelectTrigger className="w-48" data-testid="filter-access-result"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="exito">Solo éxitos</SelectItem>
                <SelectItem value="fallo">Solo fallos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="data-card overflow-x-auto">
            <Table data-testid="access-log-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Resultado</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead className="hidden lg:table-cell">Navegador</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>
                ) : filteredAccess.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Sin registros con estos filtros.</TableCell></TableRow>
                ) : (
                  filteredAccess.map((e) => (
                    <TableRow key={e.id} data-testid={`access-row-${e.id}`}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">{fechaHora(e.timestamp)}</TableCell>
                      <TableCell className="text-sm">{e.email}</TableCell>
                      <TableCell>
                        {e.success ? (
                          <Badge className="bg-success/15 text-success hover:bg-success/15 border-0">Éxito</Badge>
                        ) : (
                          <Badge className="bg-destructive/15 text-destructive hover:bg-destructive/15 border-0">Fallo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{e.reason || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{e.ip || "—"}</TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-xs truncate">{e.user_agent || "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <ScrollText className="h-4.5 w-4.5" />
                  {RESOURCE_LABELS[detail.resource_type] || detail.resource_type} — {(ACTION_META[detail.action] || {}).label || detail.action}
                </DialogTitle>
                <DialogDescription>
                  {fechaHora(detail.timestamp)} · {userName(detail.user_id) || detail.user_email || "—"}
                  {detail.ip ? ` · ${detail.ip}` : ""}
                </DialogDescription>
              </DialogHeader>
              {detail.summary && <p className="text-sm">{detail.summary}</p>}
              {detail.changes && Object.keys(detail.changes).length > 0 && (
                <div className="max-h-80 overflow-y-auto divide-y divide-border">
                  {Object.entries(detail.changes).map(([field, v]) => (
                    <div key={field} className="py-2 text-sm">
                      <p className="font-medium mb-1">{field}</p>
                      {v && typeof v === "object" && ("before" in v || "after" in v) ? (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-destructive line-through">{fmtValue(v.before)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="text-success">{fmtValue(v.after)}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">{fmtValue(v)}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
